import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { trpc } from "../lib/trpc";
import { PoDetailDrawer } from "../components/purchasing/PoDetailDrawer";
import { Icon } from "../components/ui/Icon";
import { useIsMobile } from "../lib/useIsMobile";
import { MCard } from "../components/mobile/primitives";
import "./Items.css";

interface PoRow {
  id: string;
  code: string;
  supplierName: string | null;
  status: "draft" | "confirmed" | "partially_received" | "received" | "cancelled";
  orderDate: string | null;
  expectedDate: string | null;
  lineCount: number;
}

const STATUS_BADGE: Record<PoRow["status"], { cls: string; label: string }> = {
  draft: { cls: "", label: "Taslak" },
  confirmed: { cls: "b-warn", label: "Onaylı" },
  partially_received: { cls: "b-warn", label: "Kısmi Teslim" },
  received: { cls: "b-success", label: "Teslim Alındı" },
  cancelled: { cls: "b-danger", label: "İptal" },
};

function fmtDate(d: string | null): string {
  if (!d) return "—";
  return new Intl.DateTimeFormat("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric" }).format(new Date(d));
}

export function PurchasingPage() {
  const isMobile = useIsMobile();
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const [detailId, setDetailId] = useState<string | null>(null);

  const input = useMemo(() => ({ page, pageSize, q: q.trim() || undefined }), [page, q]);
  const posQ = trpc.purchasing.list.useQuery(input);

  const rows = (posQ.data?.rows ?? []) as unknown as PoRow[];
  const total = posQ.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  if (isMobile) {
    return (
      <>
        <div className="m-stickytop">
          <Link to="/mrp" className="btn-md primary" style={{ textDecoration: "none", justifyContent: "center" }}>MRP Çalıştır →</Link>
          <div className="m-search">
            <Icon name="search" size={15} />
            <input type="search" placeholder="PO kodu, tedarikçi ara…" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} aria-label="Ara" />
          </div>
        </div>

        {posQ.isLoading ? (
          <div className="m-empty">Yükleniyor…</div>
        ) : rows.length === 0 ? (
          <div className="m-empty">{q ? `"${q}" için sonuç yok.` : "Henüz satınalma siparişi yok"}</div>
        ) : (
          <>
            {rows.map((p) => {
              const sb = STATUS_BADGE[p.status];
              const mcls = sb.cls === "b-warn" ? "b-warning" : sb.cls || "b-neutral";
              return (
                <MCard pad key={p.id} onClick={() => setDetailId(p.id)} style={{ cursor: "pointer" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span className="mono" style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 700, color: "var(--neutral-900)" }}>{p.code}</span>
                    <span className={`badge ${mcls} no-dot`}>{sb.label}</span>
                  </div>
                  <div style={{ fontSize: 13.5, fontWeight: 600, color: "var(--neutral-800)", marginTop: 6 }}>{p.supplierName ?? <span style={{ color: "var(--neutral-400)", fontWeight: 400 }}>Tedarikçi atanmadı</span>}</div>
                  <div style={{ display: "flex", gap: 16, marginTop: 8, fontSize: 11.5 }}>
                    <span><span style={{ color: "var(--neutral-400)" }}>Sipariş </span><span className="mono" style={{ color: "var(--neutral-700)" }}>{fmtDate(p.orderDate)}</span></span>
                    <span><span style={{ color: "var(--neutral-400)" }}>Satır </span><b className="mono" style={{ color: "var(--neutral-700)" }}>{p.lineCount}</b></span>
                  </div>
                </MCard>
              );
            })}
            {totalPages > 1 ? (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 12, marginTop: 2 }}>
                <button type="button" className="btn-md secondary sm" disabled={page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>‹ Önceki</button>
                <span className="mono" style={{ fontSize: 12.5, color: "var(--neutral-500)" }}>{page} / {totalPages}</span>
                <button type="button" className="btn-md secondary sm" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>Sonraki ›</button>
              </div>
            ) : null}
          </>
        )}
        <PoDetailDrawer open={!!detailId} poId={detailId} onClose={() => setDetailId(null)} />
      </>
    );
  }

  return (
    <div className="items-page">
      <div className="items-crumb">
        <Link to="/dashboard" style={{ color: "var(--neutral-500)", textDecoration: "none" }}>Panel</Link>
        <span className="sep">/</span>
        <span className="cur">Satınalma</span>
      </div>

      <div className="items-head">
        <div>
          <h1 className="title">Satınalma Siparişleri</h1>
          <p className="sub">MRP'den üretilen siparişler · onay · teslim alma (→ lot)</p>
        </div>
        <Link to="/mrp" className="btn primary" style={{ textDecoration: "none", display: "inline-flex", alignItems: "center" }}>MRP Çalıştır →</Link>
      </div>

      <div className="dt2-wrap">
        <div className="dt2-toolbar">
          <div className="dt2-search-box">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" />
            </svg>
            <input type="search" placeholder="PO kodu, tedarikçi ara…" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} aria-label="Ara" />
          </div>
          <span className="grow" />
          <button type="button" className="btn" onClick={() => posQ.refetch()} disabled={posQ.isFetching}>{posQ.isFetching ? "Yükleniyor…" : "Yenile"}</button>
        </div>

        <div className="dt2-scroll">
          {posQ.isLoading ? (
            <div className="empty-state"><p className="ttl">Yükleniyor…</p></div>
          ) : rows.length === 0 ? (
            <div className="empty-state">
              <p className="ttl">Satınalma siparişi yok</p>
              <p className="sub">{q ? `"${q}" için sonuç yok.` : "MRP çalıştırıp önerilerden PO oluşturun."}</p>
              {!q ? <Link to="/mrp" className="btn-md primary" style={{ marginTop: 12, textDecoration: "none", display: "inline-flex" }}>MRP Çalıştır</Link> : null}
            </div>
          ) : (
            <table className="dt2">
              <thead>
                <tr>
                  <th style={{ width: 150 }}>PO</th>
                  <th>Tedarikçi</th>
                  <th className="num" style={{ width: 70 }}>Satır</th>
                  <th style={{ width: 110 }}>Sipariş Tarihi</th>
                  <th style={{ width: 130 }}>Durum</th>
                  <th style={{ width: 64 }}>İşlem</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((p) => {
                  const sb = STATUS_BADGE[p.status];
                  return (
                    <tr key={p.id}>
                      <td><span className="cell-id">{p.code}</span></td>
                      <td>{p.supplierName ?? <span style={{ color: "var(--neutral-400)" }}>Tedarikçi atanmadı</span>}</td>
                      <td className="cell-num">{p.lineCount}</td>
                      <td><span className="mono" style={{ fontSize: 11.5, color: "var(--neutral-600)" }}>{fmtDate(p.orderDate)}</span></td>
                      <td><span className={`badge-pill ${sb.cls}`}>{sb.label}</span></td>
                      <td>
                        <div className="row-acts">
                          <button type="button" className="iact info" aria-label="Detay" title="Detay" onClick={() => setDetailId(p.id)}>
                            <Icon name="eye" size={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="dt2-foot">
          <div><b>{total}</b> satınalma siparişi</div>
          <div className="pager">
            <button type="button" className="pg" aria-label="Önceki sayfa" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>‹</button>
            <span className="pg active">{page}</span>
            <span style={{ color: "var(--neutral-400)", padding: "0 8px", fontSize: 12 }}>/ {totalPages}</span>
            <button type="button" className="pg" aria-label="Sonraki sayfa" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>›</button>
          </div>
        </div>
      </div>

      <PoDetailDrawer open={!!detailId} poId={detailId} onClose={() => setDetailId(null)} />
    </div>
  );
}
